import React, { useEffect, useState } from 'react';
import { Btn, C } from '../../../components/UI';
import { platformApi, TenantDetail, TenantTemplateDto, ApplyTemplateResponse } from '../../../services/api';

// Shown right after a bare tenant is created (TenantDetail from PlatformController.CreateTenant)
// — mirrors how branding/config/admin-creation are already separate follow-up Platform Admin
// actions today. Skipping leaves the tenant exactly as bare-created, same as before this feature
// existed — template use is always optional, never load-bearing.
export default function TemplatePickerModal({ tenant, onDone }: { tenant: TenantDetail; onDone: () => void }) {
    const [templates, setTemplates] = useState<TenantTemplateDto[] | null>(null);
    const [applyingKey, setApplyingKey] = useState<string | null>(null);
    const [result, setResult] = useState<ApplyTemplateResponse | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        platformApi.getTenantTemplates()
            .then(all => setTemplates(all.filter(t => t.status === 'Published')))
            .catch(e => setError(e.message || 'Failed to load templates.'));
    }, []);

    const apply = async (template: TenantTemplateDto) => {
        setApplyingKey(template.key);
        setError('');
        try {
            const res = await platformApi.applyTemplateToTenant(tenant.id, template.id);
            setResult(res);
        } catch (e: any) {
            setError(e.message || 'Failed to apply template.');
        } finally {
            setApplyingKey(null);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: C.card, borderRadius: 14, padding: 26, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}>
                {result ? (
                    <>
                        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: C.text }}>Template applied</h3>
                        <p style={{ margin: '0 0 16px', fontSize: 13, color: C.textSub }}>{tenant.name} is set up. You can still adjust anything from here.</p>
                        <div style={{ background: C.bg, borderRadius: 8, padding: 14, fontSize: 13, color: C.text, marginBottom: 18 }}>
                            <div>✓ {result.flagsApplied.length} feature flags set</div>
                            <div>✓ {result.freeAppsInstalled.length} apps installed{result.freeAppsInstalled.length > 0 ? `: ${result.freeAppsInstalled.join(', ')}` : ''}</div>
                            {result.paidAppsSuggested.length > 0 && <div>→ Suggested (not installed, tenant must subscribe): {result.paidAppsSuggested.join(', ')}</div>}
                            <div>✓ {result.pricingRulesCreated} starter pricing rules created{result.pricingRulesSkippedExisting > 0 ? ` (${result.pricingRulesSkippedExisting} skipped, already existed)` : ''}</div>
                            <div>✓ {result.serviceCatalogItemsCreated} starter catalog items created{result.serviceCatalogItemsSkippedExisting > 0 ? ` (${result.serviceCatalogItemsSkippedExisting} skipped, already existed)` : ''}</div>
                        </div>
                        <Btn onClick={onDone} fullWidth>Continue to {tenant.name}</Btn>
                    </>
                ) : (
                    <>
                        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: C.text }}>Choose a starter template</h3>
                        <p style={{ margin: '0 0 16px', fontSize: 13, color: C.textSub }}>
                            Optional — sets up feature flags, marketplace apps, and starter pricing for {tenant.name}'s business type. You can always configure everything by hand instead.
                        </p>

                        {error && <div style={{ color: '#D84040', fontSize: 13, marginBottom: 12 }}>{error}</div>}

                        {templates === null ? (
                            <div style={{ color: C.textMuted, fontSize: 13, padding: '12px 0' }}>Loading templates…</div>
                        ) : templates.length === 0 ? (
                            <div style={{ color: C.textMuted, fontSize: 13, padding: '12px 0' }}>No published templates yet.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                                {templates.map(t => (
                                    <div key={t.id} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>{t.name}</div>
                                        <div style={{ fontSize: 12.5, color: C.textSub, marginBottom: 10 }}>{t.description}</div>
                                        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 10 }}>
                                            {Object.keys(t.featureFlagOverrides).length} flags · {t.marketplaceAppKeys.length} apps ·{' '}
                                            {t.starterPricingRules.length + t.starterServiceCatalogItems.length} starter items
                                        </div>
                                        <Btn size="sm" onClick={() => apply(t)} disabled={applyingKey !== null}>
                                            {applyingKey === t.key ? 'Applying…' : 'Apply'}
                                        </Btn>
                                    </div>
                                ))}
                            </div>
                        )}

                        <Btn variant="outline" onClick={onDone} fullWidth disabled={applyingKey !== null}>
                            Skip — I'll configure manually
                        </Btn>
                    </>
                )}
            </div>
        </div>
    );
}
